<template>
    <div
        ref="selectWrapper"
        v-click-outside:[popperPaneRef]="handleClose"
        class="pt-select"
        :class="[selectSize ? 'pt-select--' + selectSize : '']"
        @click.stop="toggleMenu"
        @keydown.enter="toggleMenu"
    >
        <pt-popper
            ref="popper"
            v-model:visible="dropMenuVisible"
            placement="bottom-start"
            :append-to-body="popperAppendToBody"
            :popper-class="`pt-select__popper ${popperClass ? popperClass : ''}`"
            :fallback-placements="['bottom-start', 'top-start', 'right', 'left']"
            manual-mode
            effect="light"
            pure
            trigger="click"
            transition="pt-zoom-in-top"
            :stop-popper-mouse-event="false"
            :gpu-acceleration="false"
            @before-enter="handleMenuEnter"
        >
            <template #trigger>
                <div class="select-trigger">
                    <div
                        v-if="multiple"
                        ref="tags"
                        class="pt-select__tags"
                        :style="{ 'max-width': inputWidth - 32 + 'px', width: '100%' }"
                    >
                        <!-- //@TODO =========== -->
                        <span v-if="collapseTags && selected.length">
                            <pt-tag
                                :closable="!selectDisabled && !selected[0].isDisabled"
                                :size="collapseTagSize"
                                :hit="selected[0].hitState"
                                type="info"
                                disable-transitions
                                @close="deleteTag($event, selected[0])"
                            >
                                <span class="pt-select__tags-text" :style="{ 'max-width': inputWidth - 123 + 'px' }">{{
                                    selected[0].currentLabel
                                }}</span>
                            </pt-tag>
                            <pt-tag
                                v-if="selected.length > 1"
                                :closable="false"
                                :size="collapseTagSize"
                                type="info"
                                disable-transitions
                            >
                                <span class="pt-select__tags-text">+ {{ selected.length - 1 }}</span>
                            </pt-tag>
                        </span>
                        <transition v-if="!collapseTags" @after-leave="resetInputHeight">
                            <span :style="{ marginLeft: prefixWidth && selected.length ? `${prefixWidth}px` : null }">
                                <pt-tag
                                    v-for="item in selected"
                                    :key="getValueKey(item)"
                                    :closable="!selectDisabled && !item.isDisabled"
                                    :size="collapseTagSize"
                                    :hit="item.hitState"
                                    disable-transitions
                                    @close="deleteTag($event, item)"
                                >
                                    <span
                                        class="pt-select__tags-text"
                                        :style="{ 'max-width': inputWidth - 75 + 'px' }"
                                        >{{ item.currentLabel }}</span
                                    >
                                </pt-tag>
                            </span>
                        </transition>
                        <input
                            v-if="filterable"
                            ref="input"
                            v-model="query"
                            type="text"
                            class="pt-select__input"
                            :class="[selectSize ? `is-${selectSize}` : '']"
                            :disabled="selectDisabled"
                            :autocomplete="autocomplete"
                            :style="{
                                marginLeft:
                                    (prefixWidth && !selected.length) || tagInMultiLine ? `${prefixWidth}px` : null,
                                flexGrow: '1',
                                width: `${inputLength / (inputWidth - 32)}%`,
                                maxWidth: `${inputWidth - 42}px`,
                            }"
                            @focus="handleFocus"
                            @blur="handleBlur"
                            @keyup="managePlaceholder"
                            @keydown="resetInputState"
                            @keydown.down.prevent="navigateOptions('next')"
                            @keydown.up.prevent="navigateOptions('prev')"
                            @keydown.esc.stop.prevent="visible = false"
                            @keydown.enter.stop.prevent="handleEnter"
                            @keydown.delete="deletePrevTag"
                            @keydown.tab="visible = false"
                            @compositionstart="handleComposition"
                            @compositionupdate="handleComposition"
                            @compositionend="handleComposition"
                            @input="debouncedQueryChange"
                        />
                    </div>

                    <!--简单模式，仅展示选择内容-->
                    <div
                        class="pt-select__label"
                        :class="{ focus: visible, 'has-value': !!selectedLabel }"
                        v-if="simple"
                    >
                        <span :style="{ 'max-width': simpleLableMaxWidth }" :title="selectedLabel">
                            {{ selectedLabel || currentPlaceholder }}
                        </span>
                        <pt-icon
                            v-show="!showClose"
                            icon="pt-icon-arrow-down"
                            :class="['pt-select__caret', 'pt-input__icon', 'pt-icon-' + iconClass]"
                        ></pt-icon>
                        <pt-icon
                            v-if="showClose"
                            icon="pt-icon-clear"
                            :class="`pt-select__caret pt-input__icon ${clearIcon}`"
                            @click="handleClearClick"
                        ></pt-icon>
                    </div>

                    <pt-input
                        v-else
                        :id="id"
                        ref="reference"
                        v-model="selectedLabel"
                        type="text"
                        :placeholder="currentPlaceholder"
                        :name="name"
                        :autocomplete="autocomplete"
                        :size="selectSize"
                        :disabled="selectDisabled"
                        :readonly="readonly"
                        :validate-event="false"
                        :class="{ 'is-focus': visible }"
                        :tabindex="multiple && filterable ? '-1' : null"
                        @focus="handleFocus"
                        @blur="handleBlur"
                        @input="debouncedOnInputChange"
                        @paste="debouncedOnInputChange"
                        @keydown.down.stop.prevent="navigateOptions('next')"
                        @keydown.up.stop.prevent="navigateOptions('prev')"
                        @keydown.enter.stop.prevent="handleEnter"
                        @keydown.esc.stop.prevent="visible = false"
                        @keydown.tab="visible = false"
                        @mouseenter="inputHovering = true"
                        @mouseleave="inputHovering = false"
                    >
                        <template v-if="$slots.prefix" #prefix>
                            <div style="height: 100%; display: flex; justify-content: center; align-items: center">
                                <slot name="prefix"></slot>
                            </div>
                        </template>
                        <template #suffix>
                            <pt-icon
                                v-show="!showClose"
                                :class="['pt-select__caret', 'pt-input__icon', 'pt-icon-' + iconClass]"
                                :icon="`pt-icon-arrow-up`"
                            ></pt-icon>
                            <pt-icon
                                v-if="showClose"
                                icon="pt-icon-clear"
                                :class="`pt-select__caret pt-input__icon ${clearIcon}`"
                                @click="handleClearClick"
                            ></pt-icon>
                        </template>
                    </pt-input>
                </div>
            </template>
            <template #default>
                <pt-select-menu :size="size">
                    <pt-scrollbar
                        v-show="options.size > 0 && !loading"
                        ref="scrollbar"
                        tag="ul"
                        content-class="pt-select-dropdown__list"
                        :class="[
                            'pt-select__scrollbar',
                            { 'is-empty': !allowCreate && query && filteredOptionsCount === 0 },
                        ]"
                        :style="{
                            maxHeight: '200px',
                        }"
                    >
                        <template #default>
                            <pt-option v-if="showNewOption" :size="size" :value="query" :created="true" />
                            <slot></slot>
                        </template>
                    </pt-scrollbar>
                    <template v-if="emptyText && (!allowCreate || loading || (allowCreate && options.size === 0))">
                        <div
                            v-loading="loading"
                            v-if="loading"
                            :pt-loading-text="loadingText"
                            class="pt-select-dropdown__loading"
                        ></div>
                        <slot v-if="$slots.empty && !loading" name="empty"></slot>
                        <p v-else-if="!loading" class="pt-select-dropdown__empty">
                            {{ emptyText }}
                        </p>
                    </template>
                    <slot v-if="$slots.footer" name="footer"></slot>
                </pt-select-menu>
            </template>
        </pt-popper>
    </div>
</template>

<script lang="ts">
import ClickOutside from '../../directives/click-outside';
import PtSelectMenu from './select-dropdown.vue';
export default {
    name: 'PtSelect',
    directives: { ClickOutside },
    components: {
        PtSelectMenu,
    },
};
</script>

<script setup lang="ts">
import { toRefs, useSlots, onMounted, onBeforeUnmount, nextTick, reactive, provide, computed } from 'vue';
import { addResizeListener, removeResizeListener } from '../../../utils/resize-event';
import { t } from '../../locale';
import { UPDATE_MODEL_EVENT, CHANGE_EVENT } from '../../../utils/constants';
import { useSelect, useSelectStates } from './useSelect';
import { selectKey } from './token';

interface Props {
    name?: string;
    id?: string;
    simple?: boolean; //简单模式，显示项为span标签
    simpleLableMaxWidth?: string; //默认显示宽度
    modelValue: string | number | boolean | object;
    autocomplete?: string;
    automaticDropdown?: boolean;
    size?: 'medium' | 'small' | 'mini';
    disabled?: boolean;
    clearable?: boolean;
    filterable?: boolean;
    allowCreate?: boolean;
    loading?: boolean;
    popperClass?: string;
    remote?: boolean;
    loadingText?: string;
    noMatchText?: string;
    noDataText?: string;
    remoteMethod?: Function;
    filterMethod?: Function;
    multiple?: boolean;
    multipleLimit?: number;
    placeholder?: string;
    defaultFirstOption?: boolean;
    reserveKeyword?: boolean;
    valueKey?: string;
    collapseTags?: boolean;
    popperAppendToBody?: boolean;
    clearIcon?: string;
    popperListStyle?: any;
}

const props = withDefaults(defineProps<Props>(), {
    autocomplete: 'off',
    disabled: false,
    clearable: false,
    filterable: false,
    allowCreate: false,
    loading: false,
    remote: false,
    multiple: false,
    multipleLimit: 0,
    defaultFirstOption: false,
    reserveKeyword: false,
    valueKey: 'value',
    collapseTags: false,
    popperAppendToBody: true,
    clearIcon: 'pt-icon-clear',
    popperListStyle: {
        maxHeight: '140px',
    },
    simple: false,
    simpleLableMaxWidth: '200px',
    loadingText: '',
});

const emit = defineEmits([
    UPDATE_MODEL_EVENT,
    CHANGE_EVENT,
    'remove-tag',
    'clear',
    'visible-change',
    'focus',
    'blur',
    'enter',
]);
const slots = useSlots();
const states = useSelectStates(props);
const {
    optionsArray,
    selectSize,
    readonly,
    handleResize,
    collapseTagSize,
    debouncedOnInputChange,
    debouncedQueryChange,
    deletePrevTag,
    deleteTag,
    deleteSelected,
    handleOptionSelect,
    scrollToOption,
    setSelected,
    resetInputHeight,
    managePlaceholder,
    showClose,
    selectDisabled,
    iconClass,
    showNewOption,
    emptyText,
    toggleLastOptionHitState,
    resetInputState,
    handleComposition,
    onOptionCreate,
    onOptionDestroy,
    handleMenuEnter,
    handleFocus,
    blur,
    handleBlur,
    handleClearClick,
    handleClose,
    toggleMenu,
    selectOption,
    getValueKey,
    navigateOptions,
    dropMenuVisible,

    reference,
    input,
    popper,
    tags,
    selectWrapper,
    scrollbar,
} = useSelect(props, states, { emit: emit });

const {
    inputWidth,
    selected,
    inputLength,
    filteredOptionsCount,
    visible,
    softFocus,
    selectedLabel,
    hoverIndex,
    query,
    inputHovering,
    currentPlaceholder,
    menuVisibleOnFocus,
    isOnComposition,
    isSilentBlur,
    options,
    cachedOptions,
    optionsCount,
    prefixWidth,
    tagInMultiLine,
} = toRefs(states);

provide(
    selectKey,
    reactive({
        props,
        options,
        optionsArray,
        cachedOptions,
        optionsCount,
        filteredOptionsCount,
        hoverIndex,
        handleOptionSelect,
        selectEmitter: states.selectEmitter,
        onOptionCreate,
        onOptionDestroy,
        selectWrapper,
        selected,
        setSelected,
    }),
);

onMounted(() => {
    states.cachedPlaceHolder = currentPlaceholder.value = props.placeholder || t('pt.select.placeholder');
    if (props.multiple && Array.isArray(props.modelValue) && props.modelValue.length > 0) {
        currentPlaceholder.value = '';
    }
    addResizeListener(selectWrapper.value as any, handleResize);
    if (reference.value && reference.value.$el) {
        const sizeMap = {
            medium: 36,
            small: 32,
            mini: 28,
        };
        const input = reference.value.input;
        states.initialInputHeight = input.getBoundingClientRect().height || sizeMap[selectSize.value];
    }
    if (props.remote && props.multiple) {
        resetInputHeight();
    }
    nextTick(() => {
        if (reference.value && reference.value.$el) {
            inputWidth.value = reference.value.$el.getBoundingClientRect().width;
        }
        if (slots.prefix) {
            const inputChildNodes = reference.value.$el.childNodes;
            const input = [].filter.call(inputChildNodes, (item) => item.tagName === 'INPUT')[0];
            const prefix = reference.value.$el.querySelector('.pt-input__prefix');
            prefixWidth.value = Math.max(prefix.getBoundingClientRect().width + 5, 30);
            if (states.prefixWidth) {
                input.style.paddingLeft = `${Math.max(states.prefixWidth, 30)}px`;
            }
        }
    });
    setSelected();
});

onBeforeUnmount(() => {
    removeResizeListener(selectWrapper.value as any, handleResize);
});

if (props.multiple && !Array.isArray(props.modelValue)) {
    emit(UPDATE_MODEL_EVENT, []);
}
if (!props.multiple && Array.isArray(props.modelValue)) {
    emit(UPDATE_MODEL_EVENT, '');
}

const popperPaneRef = computed(() => {
    return popper.value?.popperRef;
});

function handleEnter(e) {
    selectOption(e);
    emit('enter');
}

defineExpose({
    reference,
    input,
    props,
    options,
    optionsArray,
    cachedOptions,
    optionsCount,
    filteredOptionsCount,
    hoverIndex,
    handleBlur,
    handleOptionSelect,
    selectEmitter: states.selectEmitter,
    onOptionCreate,
    onOptionDestroy,
    selectWrapper,
    selected,
    setSelected,
    toggleMenu,
});
</script>
<style lang="scss" src="../style/select.scss"></style>
